<template>
  <HrPageLayout
    title="Access profiles & roles"
    subtitle="Assign roles to access profiles."
    icon="🛡️"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'System Management' },
      { label: 'Access profiles & roles' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-outline-primary" @click="openAddCategory" type="button">
        <i class="bx bx-plus"></i> Add access profile
      </button>
      <button class="hr-btn hr-btn-primary" @click="loadCategories" :disabled="loading">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="category-role-content">
      <div v-if="loadError" class="hr-alert hr-alert-danger hr-mb-3" style="padding:12px 16px;background:#fef2f2;color:#991b1b;border-radius:8px;">
        {{ loadError }}
        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" style="margin-left:8px;" @click="retryLoad">Retry</button>
      </div>

      <div v-if="loadingRoles" class="hr-text-center hr-py-5">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted hr-mt-2">Loading all roles...</p>
      </div>

      <template v-else>
        <div class="hr-mb-4">
          <p class="hr-text-muted hr-mb-0">
            <strong>{{ allRoles.length }}</strong> roles. Select an access profile to manage its roles.
          </p>
        </div>

        <div v-if="loading" class="hr-text-center hr-py-4">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading categories...</p>
        </div>

        <div v-else class="hr-content-grid" style="grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));">
          <div
            v-for="cat in categories"
            :key="cat.id"
            class="hr-chart-card"
          >
            <div class="hr-chart-header">
                <h5 class="hr-chart-title category-name">
                <i class="bx bx-folder"></i> {{ cat.categoryName }}
              </h5>
              <div class="hr-d-flex hr-gap-2">
                <button
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-outline-primary"
                  @click="openEditCategory(cat)"
                  title="Edit category"
                >
                  <i class="bx bx-edit-alt"></i>
                </button>
                <button
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-primary"
                  @click="openManageRoles(cat)"
                >
                  <i class="bx bx-shield-plus"></i> Manage Roles
                </button>
              </div>
            </div>
            <div class="hr-chart-body">
              <div class="hr-d-flex hr-justify-between hr-mb-2">
                <span class="hr-text-muted">Users</span>
                <span>{{ cat.currentUsers || 0 }}{{ cat.usersLimit > 0 ? ' / ' + cat.usersLimit : '' }}</span>
              </div>
              <div class="hr-d-flex hr-justify-between">
                <span class="hr-text-muted">Roles assigned</span>
                <span class="hr-badge hr-badge-info">{{ cat.rolesCount || 0 }}</span>
              </div>
              <p v-if="cat.description" class="hr-small hr-text-muted hr-mt-2 hr-mb-0">{{ cat.description }}</p>
            </div>
          </div>

          <div v-if="categories.length === 0" class="hr-chart-card" style="grid-column: 1 / -1;">
            <div class="hr-chart-body hr-text-center hr-py-5">
              <i class="bx bx-folder-open hr-text-muted" style="font-size: 48px;"></i>
              <p class="hr-text-muted hr-mt-2 hr-mb-0">No access profiles found.</p>
              <button type="button" class="hr-btn hr-btn-primary hr-mt-3" @click="openAddCategory">
                <i class="bx bx-plus"></i> Add access profile
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <AddEditCategoryModal
      v-if="showCategoryModal"
      :category="editingCategory"
      :mode="categoryModalMode"
      @close="showCategoryModal = false; editingCategory = null"
      @saved="onCategorySaved"
    />
    <ManageRolesModal
      v-if="showRolesModal && selectedCategory"
      :category="selectedCategory"
      :all-roles="allRoles"
      @close="showRolesModal = false; selectedCategory = null"
      @saved="onRolesSaved"
    />
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, fetchPermissionsAndMenus } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import AddEditCategoryModal from "@/components/employeeRegister/modals/AddEditCategoryModal.vue";
import ManageRolesModal from "@/components/employeeRegister/modals/ManageRolesModal.vue";

export default {
  name: "CategoryRoleManagement",
  components: { HrPageLayout, AddEditCategoryModal, ManageRolesModal },
  data() {
    return {
      loading: false,
      loadingRoles: true,
      loadError: "",
      categories: [],
      allRoles: [],
      showRolesModal: false,
      selectedCategory: null,
      showCategoryModal: false,
      editingCategory: null,
      categoryModalMode: 'add' // 'add' | 'edit'
    };
  },
  mounted() {
    this.fetchAllRoles();
    this.loadCategories();
  },
  methods: {
    retryLoad() {
      this.loadError = "";
      this.fetchAllRoles();
      this.loadCategories();
    },
    extractPayload(res) {
      const body = res?.data || {};
      // Support { success, data: { categories } } and flat shapes
      if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
        return { success: body.success !== false, payload: body.data, message: body.message };
      }
      return { success: body.success !== false, payload: body, message: body.message };
    },
    fetchAllRoles() {
      this.loadingRoles = true;
      const data = new FormData();
      data.append("function", "getAllRoles");
      execute(data, authUrl)
        .then((res) => {
          const body = res?.data || {};
          const payload = body.data != null ? body.data : body;
          let roles = [];
          if (Array.isArray(payload?.roles)) roles = payload.roles;
          else if (Array.isArray(payload?.data?.roles)) roles = payload.data.roles;
          else if (Array.isArray(payload)) roles = payload;
          else if (Array.isArray(body?.roles)) roles = body.roles;
          this.allRoles = roles;
          if (!roles.length) {
            const msg = body.message || payload?.message || "";
            // Only surface as error if API failed; empty success is rare
            if (body.success === false) {
              this.loadError = msg || "Failed to load roles (getAllRoles).";
            } else if (msg && !this.loadError) {
              // keep quiet for empty DB; still show count 0
            }
          } else if (!this.loadError) {
            // roles ok
          }
          this.loadingRoles = false;
        })
        .catch((e) => {
          this.allRoles = [];
          this.loadingRoles = false;
          this.loadError =
            e?.response?.data?.message ||
            e?.message ||
            "Network error loading roles. Check API connectivity.";
        });
    },
    loadCategories() {
      this.loading = true;
      this.fetchCategories("getCategoriesWithUserCount")
        .then((cats) => {
          if (cats.length) {
            this.categories = cats;
            this.loading = false;
            this.loadError = "";
            return null;
          }
          return this.fetchCategories("getCategories");
        })
        .then((cats) => {
          if (cats == null) return;
          this.categories = Array.isArray(cats) ? cats : [];
          this.loading = false;
          if (!this.categories.length) {
            this.loadError =
              this.loadError ||
              "API returned no access profiles. Confirm you are online and your session is valid, then click Refresh.";
          } else {
            this.loadError = "";
          }
        })
        .catch((e) => {
          this.categories = [];
          this.loading = false;
          this.loadError =
            e?.response?.data?.message ||
            e?.message ||
            "Network error loading access profiles.";
        });
    },
    fetchCategories(fnName) {
      const data = new FormData();
      data.append("function", fnName);
      return execute(data, authUrl).then((res) => {
        const body = res?.data || {};
        if (body.success === false) {
          const err = new Error(body.message || `Failed (${fnName})`);
          err.response = res;
          // For primary call, try fallback; only throw if both fail
          if (fnName === "getCategories") throw err;
          return [];
        }
        const payload = body.data != null ? body.data : body;
        let cats = [];
        if (Array.isArray(payload?.categories)) cats = payload.categories;
        else if (Array.isArray(payload?.data?.categories)) cats = payload.data.categories;
        else if (Array.isArray(payload)) cats = payload;
        else if (Array.isArray(body?.categories)) cats = body.categories;
        return cats;
      });
    },
    openAddCategory() {
      this.editingCategory = null;
      this.categoryModalMode = 'add';
      this.showCategoryModal = true;
    },
    openEditCategory(category) {
      this.editingCategory = { ...category };
      this.categoryModalMode = 'edit';
      this.showCategoryModal = true;
    },
    onCategorySaved() {
      this.showCategoryModal = false;
      this.editingCategory = null;
      this.loadCategories();
    },
    openManageRoles(category) {
      this.selectedCategory = { id: category.id, categoryName: category.categoryName };
      this.showRolesModal = true;
    },
    onRolesSaved() {
      this.showRolesModal = false;
      const savedCategoryId = this.selectedCategory?.id != null ? String(this.selectedCategory.id) : null;
      this.selectedCategory = null;
      this.loadCategories();
      this.refreshCurrentSessionAccessIfNeeded(savedCategoryId);
    },
    refreshCurrentSessionAccessIfNeeded(savedCategoryId) {
      try {
        const raw = sessionStorage.getItem("userInfo") || "{}";
        const userInfo = JSON.parse(raw);
        const currentCategoryId = String(
          userInfo?.categoryId ?? userInfo?.categoryID ?? userInfo?.category_id ?? ""
        );
        if (!currentCategoryId) {
          const ctxRaw = sessionStorage.getItem("rbac_context") || "{}";
          const ctx = JSON.parse(ctxRaw);
          const ctxCategoryId = String(ctx?.category_id ?? "");
          if (!savedCategoryId || !ctxCategoryId || savedCategoryId !== ctxCategoryId) return;
        } else if (!savedCategoryId || savedCategoryId !== currentCategoryId) {
          return;
        }
      } catch (_) {
        return;
      }

      const data = new FormData();
      data.append("function", "refreshUserRoles");
      execute(data, authUrl)
        .then((res) => {
          const roles = res?.data?.data?.roles;
          if (Array.isArray(roles)) {
            sessionStorage.setItem("selectedRoles", JSON.stringify(roles));
          }
        })
        .catch(() => {})
        .finally(() => {
          fetchPermissionsAndMenus(authUrl, execute).catch(() => {});
        });
    }
  }
};
</script>

<style scoped>
.category-role-content {
  min-height: 200px;
}

.category-name {
  overflow-wrap: anywhere;
}
</style>
